import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@heroui/react';
import { CommentItem, CommentItemSkeleton } from '~/components/molecules/CommentItem';
import { useReviewComments } from '~/hooks/useReviewComments';

interface CommentsListProps {
  reviewId: string;
  initialLimit?: number;
  onNavigateToUser: () => void;
}

export const CommentsList: React.FC<CommentsListProps> = ({
  reviewId,
  initialLimit = 5,
  onNavigateToUser,
}) => {
  const { t } = useTranslation();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    comments,
    commentsCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    deleteComment,
    isDeletingComment,
  } = useReviewComments(reviewId, initialLimit);
  
  const handleLoadMore = () => {
    fetchNextPage();
  };
  
  const handleDeleteComment = (commentId: string) => {
    deleteComment(commentId);
  };
  
  if (isLoading && !comments.length) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <CommentItemSkeleton key={index} />
        ))}
      </div>
    );
  }
  
  if (!comments || comments.length === 0) {
    return (
      <div className="text-center py-6 text-secondary-500">
        {t('comments.noComments')}
      </div>
    );
  }
  
  return (
    <div className="w-full">
      <div className="space-y-1">
        {comments.map(comment => (
          <CommentItem
            onNavigateToUser={onNavigateToUser}
            key={comment.id}
            comment={comment}
            onDelete={handleDeleteComment}
          />
        ))}
      </div>
      
      {/* Loading more indicator */}
      {isFetchingNextPage && (
        <div className="space-y-2 mt-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <CommentItemSkeleton key={`loading-more-${index}`} />
          ))}
        </div>
      )}
      
      {/* Delete indicator */}
      {isDeletingComment && (
        <div className="flex justify-center items-center py-3">
          <Spinner size="sm" color="warning" />
        </div>
      )}
      
      {/* Load more button */}
      {hasNextPage && !isDeletingComment && !isFetchingNextPage && (
        <div className="flex justify-center mt-4" ref={loadMoreRef}>
          <Button
            variant="flat"
            color="secondary"
            size="sm"
            onPress={handleLoadMore}
          >
            {t('comments.loadMore')}
          </Button>
        </div>
      )}

      {!hasNextPage && comments.length > 0 && commentsCount > initialLimit && (
        <div className="text-center text-xs text-secondary-500 mt-4">
          {t('common.reachedEndOfContent')}
        </div>
      )}
    </div>
  );
}; 